import type { Dispatch } from "react";
import { useTranslation } from "@repo/i18n";
import type { BookingConfig } from "../../config/types";
import { Field } from "../components/Field";
import { PartyStepper } from "../components/PartyStepper";
import { addDaysIso, todayInTz } from "../format";
import type { FlowAction, FlowState } from "../flow/state";

type SearchState = Extract<FlowState, { step: "SEARCH" }>;

/**
 * SEARCH: date + party-size. Only bookable dates are offerable — the picker `min` is today in the
 * VENUE timezone and `max` is `today + horizon`, so lead-time/horizon-violating dates are never
 * selectable (spec edge case). A party above the service max flips to the large-party REQUEST path
 * (R1/SC-5) — submitting `START_REQUEST` and NEVER a slot query.
 */
export function Search({
  state,
  dispatch,
  booking,
  timeZone,
}: {
  state: SearchState;
  dispatch: Dispatch<FlowAction>;
  booking: BookingConfig;
  timeZone: string;
}) {
  const { t } = useTranslation();
  const { serviceDay, partySize } = state.search;
  const minDate = todayInTz(timeZone);
  const maxDate = addDaysIso(minDate, booking.horizonDays);
  const isLarge = partySize > booking.partySizeMax;
  const canSubmit = serviceDay !== null && serviceDay.length > 0;

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    if (isLarge) dispatch({ type: "START_REQUEST", idempotencyKey: crypto.randomUUID() });
    else dispatch({ type: "SEARCH_SUBMIT" });
  };

  return (
    <form className="rsv-step" onSubmit={onSubmit} aria-label={t("widget.booking.search.title")}>
      <h2>{t("widget.booking.search.title")}</h2>

      <Field id="rsv-date" label={t("widget.booking.search.date")}>
        {(aria) => (
          <input
            {...aria}
            className="rsv-input"
            type="date"
            min={minDate}
            max={maxDate}
            value={serviceDay ?? ""}
            onChange={(e) => dispatch({ type: "SET_DATE", serviceDay: e.target.value || null })}
          />
        )}
      </Field>

      <PartyStepper
        value={partySize}
        max={booking.partySizeMax}
        labelId="rsv-party-label"
        onChange={(next) => dispatch({ type: "SET_PARTY", partySize: next })}
      />

      <button type="submit" className="rsv-button" disabled={!canSubmit}>
        {isLarge ? t("widget.booking.search.request") : t("widget.booking.search.submit")}
      </button>
    </form>
  );
}
